import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';

/**
 * Serialize the current selection to Markdown using the @tiptap/markdown
 * storage serializer. Returns null when selection is collapsed or empty.
 */
export function selectionToMarkdown(editor: Editor): string | null {
  return serializeSelection(editor, (doc) => doc);
}

interface SliceNode {
  type?: string;
  content?: SliceNode[];
}

function withoutEmbeds(node: SliceNode): SliceNode {
  if (!node.content) return node;
  return {
    ...node,
    content: node.content.filter((child) => child.type !== 'youtube').map(withoutEmbeds),
  };
}

/** The selection as a template passage: embeds carry no text, so none is sent. */
export function selectionToCaptureMarkdown(editor: Editor): string | null {
  return serializeSelection(editor, withoutEmbeds);
}

function serializeSelection(editor: Editor, shape: (doc: SliceNode) => SliceNode): string | null {
  const { selection } = editor.state;
  if (selection.empty) return null;

  const { from, to } = selection;
  const slice = editor.state.doc.cut(from, to);
  // @tiptap/markdown v3 exposes the serializer on storage.markdown.manager,
  // not directly on storage.markdown (LUMEN-3V).
  const markdown = (
    editor.storage.markdown as unknown as { manager: { serialize: (doc: unknown) => string } }
  ).manager.serialize(shape(slice.toJSON() as SliceNode));
  return markdown || null;
}

/**
 * MarkdownCopy — intercepts copy/cut and writes Markdown to the clipboard
 * instead of HTML. This keeps the paste side on the deterministic
 * markdown-import path (MarkdownPaste detects and converts it).
 *
 * Mirrors the Lexical MarkdownCopyPlugin, ported to a ProseMirror plugin
 * so it integrates cleanly with Tiptap's event model.
 */
export const MarkdownCopy = Extension.create({
  name: 'markdownCopy',

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('markdownCopy'),
        props: {
          handleDOMEvents: {
            copy: (_view, event) => {
              const markdown = selectionToMarkdown(editor);
              if (!markdown) return false;
              const e = event as ClipboardEvent;
              if (e.clipboardData) {
                e.preventDefault();
                e.clipboardData.clearData?.();
                e.clipboardData.setData('text/plain', markdown);
                return true;
              }
              navigator.clipboard.writeText(markdown).catch(console.error);
              return true;
            },
            cut: (_view, event) => {
              const markdown = selectionToMarkdown(editor);
              if (!markdown) return false;
              const e = event as ClipboardEvent;
              if (e.clipboardData) {
                e.clipboardData.clearData?.();
                e.clipboardData.setData('text/plain', markdown);
              }
              // Return false — let ProseMirror handle the deletion
              return false;
            },
          },
        },
      }),
    ];
  },
});
